/**
 * Pure maths and copy of the pricing calculator. Every figure comes from the tier rows
 * (GET /api/slots/tiers, or the published list prices) through lib/slotPricing, the same
 * functions checkout mirrors (backend services/slot_billing.py): the multi-venue discount
 * (10% at 2-3 venues, 20% at 4+) applies to quarterly billing and to Venue Sponsor (always
 * quarterly); annual prepay (Standard, Premium) has its months free and no multi-venue
 * discount. Nothing here hard-codes a price.
 */
import {
  PAID_SLOT_TIERS,
  annualMonthsFree,
  discountCents,
  formatCount,
  multiVenueDiscountPct,
  multiVenueDiscountPctFor,
  multiVenuePrice,
  perMonthCents,
  quarterlyCents,
} from '@/lib/slotPricing';

export const MIN_VENUES = 1;
export const MAX_VENUES = 20;
export const INTERVALS = ['quarterly', 'annual'];

/** A whole number of venues within 1-20 (anything else → the nearest bound, NaN → 1). */
export function clampVenues(n) {
  const v = Math.trunc(Number(n));
  if (!Number.isFinite(v)) return MIN_VENUES;
  return Math.min(MAX_VENUES, Math.max(MIN_VENUES, v));
}

const cents = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/** True when this tier can be prepaid annually (Standard/Premium with an annual price). */
export function hasAnnual(tier) {
  return Boolean(tier) && tier.tier_key !== 'sponsor' && cents(tier.annual_price_cents) > 0;
}

/**
 * The figures of one plan card for `interval` and `venues`:
 * { key, interval (the one billed: 'quarterly' | 'annual'), quarterlyOnly (asked for annual,
 *   billed quarterly: Venue Sponsor), venues,
 *   monthlyTotalCents (the card's big figure: a month for all the venues — quarterly: the
 *   discounted monthly price x venues; annual: the yearly total / 12),
 *   perVenueMonthlyCents (the same for one of those venues), billCents (billed up front for
 *   all venues: a quarter or a year), billPeriod ('quarter' | 'year'), pct, discountCents
 *   (per bill), monthsFree, commitmentTotalCents (Sponsor: 4 quarterly bills, else null) }
 */
export function planFigures(tier, interval, venues) {
  const key = tier.tier_key;
  const n = clampVenues(venues);
  if (interval === 'annual' && hasAnnual(tier)) {
    const bill = multiVenuePrice(tier.annual_price_cents, n, 'annual', key);
    return {
      key,
      interval: 'annual',
      quarterlyOnly: false,
      venues: n,
      monthlyTotalCents: perMonthCents(bill.total, 12),
      perVenueMonthlyCents: perMonthCents(tier.annual_price_cents, 12),
      billCents: bill.total,
      billPeriod: 'year',
      pct: bill.pct,
      discountCents: bill.discount,
      monthsFree: annualMonthsFree(tier),
      commitmentTotalCents: null,
    };
  }
  const bill = multiVenuePrice(quarterlyCents(tier), n, 'quarterly', key);
  const month = multiVenuePrice(tier.monthly_price_cents, n, 'quarterly', key);
  const monthly = cents(tier.monthly_price_cents);
  return {
    key,
    interval: 'quarterly',
    quarterlyOnly: interval === 'annual',
    venues: n,
    monthlyTotalCents: month.total,
    perVenueMonthlyCents: monthly - discountCents(monthly, bill.pct),
    billCents: bill.total,
    billPeriod: 'quarter',
    pct: bill.pct,
    discountCents: bill.discount,
    monthsFree: 0,
    commitmentTotalCents: key === 'sponsor' ? bill.total * 4 : null,
  };
}

/**
 * What annual prepay saves in a year against four quarterly bills for the same venues (the
 * quarterly multi-venue discount included), in cents; 0 when annual does not apply or saves
 * nothing. One Standard venue: 4 × $1,347 − $4,490 = $898 (upgrade brief §3.5).
 */
export function annualSavingCents(tier, venues) {
  if (!hasAnnual(tier)) return 0;
  const annual = planFigures(tier, 'annual', venues);
  const quarterly = planFigures(tier, 'quarterly', venues);
  const saving = quarterly.billCents * 4 - annual.billCents;
  return saving > 0 ? saving : 0;
}

/** The paid tiers of a catalogue, in display order (free listing skipped). */
export const paidTiers = (tiers) => (tiers || []).filter((t) => PAID_SLOT_TIERS.includes(t.tier_key));

/** "1 month free" / "2 months free". */
export const monthsFreeText = (n) => (n === 1 ? '1 month free' : `${n} months free`);

/**
 * The annual saving of Standard and Premium in words: "2 months free", "up to 2 months
 * free" when they differ, '' when annual saves nothing.
 */
export function annualFreeText(tiers) {
  const free = paidTiers(tiers).filter(hasAnnual).map(annualMonthsFree);
  const max = Math.max(0, ...free);
  if (!max) return '';
  return `${free.every((m) => m === max) ? '' : 'up to '}${monthsFreeText(max)}`;
}

/**
 * The multi-venue discount bands from slotPricing, in words:
 * "10% off every slot at 2 or 3 venues and 20% off at 4 or more".
 */
export function discountBandsText() {
  const bands = [];
  for (let n = MIN_VENUES; n <= MAX_VENUES + 1; n += 1) {
    const pct = multiVenueDiscountPct(n);
    const last = bands[bands.length - 1];
    if (pct > 0 && last && last.pct === pct) last.to = n;
    else if (pct > 0) bands.push({ pct, from: n, to: n });
  }
  const phrase = (b, i) => {
    const open = b.to > MAX_VENUES;
    let where;
    if (open) where = `${b.from} or more`;
    else if (b.to === b.from) where = `${b.from} venues`;
    else if (b.to === b.from + 1) where = `${b.from} or ${b.to} venues`;
    else where = `${b.from}–${b.to} venues`;
    return `${b.pct}% off${i === 0 ? ' every slot' : ''} at ${where}`;
  };
  return bands.map(phrase).join(' and ');
}

/**
 * The live summary next to the venue stepper (read out when it changes):
 * "1 venue: base price, billed quarterly", "3 venues: 10% off every slot billed quarterly",
 * "Annual prepay: 2 months free, no multi-venue discount" (+ Venue Sponsor's quarterly
 * discount at 2+ venues when Sponsor is shown).
 */
export function calculatorSummary(interval, venues, tiers) {
  const n = clampVenues(venues);
  if (interval === 'annual') {
    const free = annualFreeText(tiers);
    let text = `Annual prepay: ${free ? `${free}, ` : ''}no multi-venue discount`;
    const sponsorPct = multiVenueDiscountPctFor(n, 'annual', 'sponsor');
    if (sponsorPct && paidTiers(tiers).some((t) => t.tier_key === 'sponsor')) {
      text += `; Venue Sponsor stays quarterly with ${sponsorPct}% off`;
    }
    return text;
  }
  const pct = multiVenueDiscountPctFor(n, 'quarterly');
  const v = `${n} ${n === 1 ? 'venue' : 'venues'}`;
  return pct ? `${v}: ${pct}% off every slot billed quarterly` : `${v}: base price, billed quarterly`;
}

/** The note under the cards (the design's #pricing-note, made true to the billing rules). */
export function pricingNote(interval, tiers) {
  const bands = discountBandsText();
  const free = annualFreeText(tiers);
  if (interval === 'annual') {
    return `*Annual Standard and Premium are paid a year up front${free ? ` with ${free}` : ''} and no multi-venue discount; the two never combine. `
      + `Venue Sponsor is billed quarterly on a 12-month commitment, with ${bands}. Coaster quantities are per venue, per month.`;
  }
  return `Quarterly prices include ${bands}, Venue Sponsor included. Coaster quantities are per venue, per month. `
    + `*Annual prepay is for Standard and Premium only${free ? `, with ${free}` : ''} and no multi-venue discount.`;
}

/**
 * Card copy per tier: the design's labels and lines, with truthful per-month coaster figures.
 * `label` is factual (Premium's orange card says what it is, never "Most popular"; upgrade
 * brief §3.5); `choose` is the card's CTA ("Choose {Tier}", §2.2): it opens the enquiry
 * dialog with that tier pre-selected.
 */
export const PLAN_COPY = {
  standard: {
    label: 'A shared placement',
    description: 'A local introduction.',
    choose: 'Choose Standard',
    lines: (tier) => {
      const n = Number(tier.coasters_per_venue) > 0 ? formatCount(tier.coasters_per_venue) : '';
      return [
        n ? `Your logo tile on the shared back of ${n} coasters a month, per venue` : 'Your logo tile on the shared back of the coasters, every month',
        'Business profile and one offer',
        'Monthly reports',
      ];
    },
  },
  premium: {
    label: 'Your own coaster front.',
    description: 'Room to tell your story.',
    choose: 'Choose Premium',
    lines: (tier) => {
      const n = Number(tier.coasters_per_venue) > 0 ? formatCount(tier.coasters_per_venue) : '';
      return [
        n ? `Your own front on ${n} coasters a month, per venue` : 'Your own front on the coasters, every month',
        'Featured offer card, landing page and lead form',
        'Monthly reports',
      ];
    },
  },
  sponsor: {
    label: 'Both sides. All you.',
    description: 'Make yourself at home.',
    choose: 'Choose Venue Sponsor',
    lines: (tier) => {
      const n = Number(tier.coasters_per_venue) > 0 ? formatCount(tier.coasters_per_venue) : '';
      return [
        n ? `${n} double-sided coasters a month, per venue, with your creative on both sides` : 'Double-sided coasters every month, with your creative on both sides',
        'Top banner and a microsite of up to 3 pages',
        'One Venue Sponsor per venue, with monthly reports',
      ];
    },
  },
};

/** The plan context a "Choose {Tier}" button sends to the enquiry dialog (the tier pre-selected). */
export function planContextFor(tier, interval, venues) {
  const f = planFigures(tier, interval, venues);
  return { tier: tier.tier_key, interval: f.interval, venue_count: f.venues };
}

/** The coaster photo per tier (upgrade brief §3.1 item 7, §3.3 item 4, §3.5): what the tier looks like. */
export const TIER_PHOTOS = Object.freeze({ standard: 'BIZ-TIER-01', premium: 'BIZ-TIER-02', sponsor: 'BIZ-TIER-03' });

/**
 * The line under each tier's example coaster (owner coaster package 2026-09-28). Always says it
 * is an example; Standard says it is ONE of the logo spaces (`spaces` = the live
 * slots_per_venue); the Venue Sponsor example keeps MAVEN's offer and its condition together.
 */
export function tierExample(tier, spaces) {
  const n = Number(spaces) > 0 ? Number(spaces) : 0;
  if (tier === 'standard') {
    return n
      ? `Example coaster: your logo takes one of the ${n} spaces on the shared back, like the one ringed in orange.`
      : 'Example coaster: your logo takes one space on the shared back, like the one ringed in orange.';
  }
  if (tier === 'premium') return 'Example coaster: one business owns the whole front.';
  if (tier === 'sponsor') {
    // MAVEN's own offer to ITS customers (owner-confirmed terms), never Tappd's pricing: the
    // condition always goes with it.
    return 'Example coaster: MAVEN owns the front and the back, where it runs its own offer to its customers: 3 months free with an annual commitment.';
  }
  return '';
}
